import { Button } from "antd";
import styled, { css } from "styled-components";
import type { GlobalToken } from "antd";
import { radius } from "@libertify/ui";

export interface TokenProps {
  $token: GlobalToken;
}

// The "Create now" bar is position: fixed at the bottom-right (28px offset,
// ~64px tall). The bottom padding here reserves enough empty space so the last
// content in this scroll container can always clear the bar instead of hiding
// behind it.
const CREATE_BAR_CLEARANCE = 110;

// Playbook-edit mode has no "Create now" bar, so it needs no clearance.
export const Shell = styled.div<{ $hasCreateBar: boolean }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 32px 40px
    ${({ $hasCreateBar }) => ($hasCreateBar ? CREATE_BAR_CLEARANCE : 32)}px;
  overflow-y: auto;

  @media (width <= 900px) {
    padding: 24px 20px
      ${({ $hasCreateBar }) => ($hasCreateBar ? CREATE_BAR_CLEARANCE : 24)}px;
  }
`;

export const Title = styled.h1<TokenProps>`
  font-size: ${({ $token }) => $token.fontSizeHeading2}px;
  font-weight: 300;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: ${({ $token }) => $token.colorText};
  margin: 0;
`;

// Two columns: the settings/add-on rail (left) + the config card (right).
export const Body = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (width <= 900px) {
    flex-direction: column;
  }
`;

export const RightCol = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

export const CardTitle = styled.h2<TokenProps>`
  font-size: ${({ $token }) => $token.fontSizeHeading4}px;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: ${({ $token }) => $token.colorText};
  margin: 0 0 6px;
`;

export const CardPlaceholder = styled.p<TokenProps>`
  font-size: ${({ $token }) => $token.fontSize}px;
  color: ${({ $token }) => $token.colorTextTertiary};
  margin: 0;
`;

export const EmptyBody = styled.div`
  min-height: 320px;
`;

/**
 * Playbook-edit warning, matching the design mock's amber notice literally.
 *
 * Hard-coded rather than themed because antd derives its warning palette from
 * the seed and drifts off the mock (`colorWarningBg` resolves to #fffae6, and
 * the only warning colour dark enough for body text is #cf7c00 against the
 * mock's #92400e). theme-v2's own palette does carry #FFFBEB, but it is not
 * re-exported from `@libertify/ui`. Same call SavePlaybookModal made.
 */
export const EditNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px;
  border: 1px solid #fde68a;
  border-radius: ${radius.box}px;
  background: #fffbeb;
  color: #92400e;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.28px;
  line-height: 1.5;

  b {
    font-weight: 700;
  }
`;

export const EditNoticeIcon = styled.span`
  display: flex;
  flex: none;
`;

// ─── Playbook name + description, edited in place (edit mode only) ───

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const HeadSub = styled.p<TokenProps>`
  margin: 0;
  max-width: 720px;
  font-size: 15px;
  letter-spacing: -0.3px;
  color: ${({ $token }) => $token.colorTextTertiary};
  line-height: 1.4;
`;

export const TitleEditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

// Sized to the heading it replaces, so swapping between the two doesn't move
// the page. Colours come from tokens — unlike the amber notice above, the
// mock's mono/green here map straight onto them.
export const TitleInput = styled.input<TokenProps>`
  width: 100%;
  max-width: 640px;
  padding: 10px 16px;
  border: 1.5px solid ${({ $token }) => $token.colorBorder};
  border-radius: 12px;
  background: ${({ $token }) => $token.colorBgContainer};
  color: ${({ $token }) => $token.colorText};
  font-family: inherit;
  font-size: ${({ $token }) => $token.fontSizeHeading2}px;
  font-weight: 300;
  letter-spacing: -0.03em;
  line-height: 1.1;

  &:focus {
    outline: none;
    border-color: ${({ $token }) => $token.colorPrimary};
  }

  @media (width <= 600px) {
    font-size: ${({ $token }) => $token.fontSizeHeading3}px;
  }
`;

export const DescInput = styled.textarea<TokenProps>`
  width: 100%;
  max-width: 640px;
  min-height: 64px;
  padding: 9px 14px;
  border: 1.5px solid ${({ $token }) => $token.colorBorder};
  border-radius: 10px;
  background: ${({ $token }) => $token.colorBgContainer};
  color: ${({ $token }) => $token.colorTextSecondary};
  font-family: inherit;
  font-size: 15px;
  letter-spacing: -0.3px;
  line-height: 1.4;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ $token }) => $token.colorPrimary};
  }
`;

/**
 * The design mock's pill buttons: 36px tall, fully rounded, 14px/600 with
 * tight tracking. Not the design system's `Button`, whose every variant marks
 * its hover and active colours `!important`; and doubled selectors here
 * because antd injects its own button CSS at runtime and would otherwise win
 * on source order (the same trap that left the ⋮ trigger 40px wide).
 *
 * Structure is the mock's literally; colours stay on tokens, theme-v2 being
 * the app's own version of the mock's mono ramp and green family.
 */
const pill = css<TokenProps>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  border-radius: ${radius.pill}px;
  box-shadow: none;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.28px;
  white-space: nowrap;
`;

export const PillGhostButton = styled(Button)<TokenProps>`
  && {
    ${pill}
    padding: 0 16px;
    border: 1px solid ${({ $token }) => $token.colorBorder};
    background: ${({ $token }) => $token.colorBgContainer};
    color: ${({ $token }) => $token.colorTextSecondary};

    &:hover:not(:disabled) {
      border-color: ${({ $token }) => $token.colorPrimary};
      color: ${({ $token }) => $token.colorPrimaryHover};
    }
  }
`;

export const PillPrimaryButton = styled(Button)<TokenProps>`
  && {
    ${pill}
    padding: 0 18px;
    border: none;
    background: ${({ $token }) => $token.colorPrimary};
    color: ${({ $token }) => $token.colorWhite};

    &:disabled {
      background: ${({ $token }) => $token.colorBorder};
      color: ${({ $token }) => $token.colorTextQuaternary};
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background: ${({ $token }) => $token.colorPrimaryHover};
      color: ${({ $token }) => $token.colorWhite};
    }
  }
`;
